<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { GraduationCap, BookOpen, ArrowUpRight, ChevronDown } from 'lucide-vue-next'

interface MicrolearningBlock {
    skills_url: string
    incomplete: string[]
}

const PAGE_SIZE = 10

const page = usePage()
const { t } = useI18n()

const block = computed<MicrolearningBlock | null>(
    () => (page.props.microlearningBlock as MicrolearningBlock | null | undefined) ?? null,
)

const visibleCount = ref(PAGE_SIZE)

const incomplete = computed(() => block.value?.incomplete ?? [])
const visibleItems = computed(() => incomplete.value.slice(0, visibleCount.value))
const remaining = computed(() => Math.max(0, incomplete.value.length - visibleCount.value))

function showMore() {
    visibleCount.value += PAGE_SIZE
}

// Only the host (e.g. "skills.marketscale.com") shows as the inline link text.
const skillsHost = computed(() => {
    const url = block.value?.skills_url ?? 'https://skills.marketscale.com'
    try {
        return new URL(url).host
    } catch {
        return 'skills.marketscale.com'
    }
})

// Lock the underlying page scroll while the takeover is shown, and reset the
// "show more" window whenever the block appears/changes.
watch(
    block,
    (value) => {
        visibleCount.value = PAGE_SIZE
        document.body.style.overflow = value ? 'hidden' : ''
    },
    { immediate: true },
)

onUnmounted(() => {
    document.body.style.overflow = ''
})
</script>

<template>
    <Teleport to="body">
        <!-- Opaque takeover starting below the navbar (keeps the navbar visible,
             hides all page content). Styled with scoped CSS so it renders
             consistently regardless of Tailwind coverage on the underlying page. -->
        <div
            v-if="block"
            class="mlb"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="mlb-title"
        >
            <div class="mlb__card">
                <div class="mlb__body">
                    <!-- Header -->
                    <div class="mlb__header">
                        <div class="mlb__badge">
                            <GraduationCap :size="24" :stroke-width="2" />
                        </div>
                        <div class="mlb__header-text">
                            <h2 id="mlb-title" class="mlb__title">
                                {{ t('microlearning.blocked_title') }}
                            </h2>
                            <p class="mlb__intro">
                                {{ t('microlearning.blocked_message_prefix')
                                }}<a
                                    :href="block.skills_url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="mlb__link"
                                    >{{ skillsHost }}</a
                                >{{ t('microlearning.blocked_message_suffix') }}
                            </p>
                        </div>
                    </div>

                    <!-- Modules list -->
                    <div v-if="incomplete.length" class="mlb__section">
                        <div class="mlb__section-head">
                            <BookOpen class="mlb__section-icon" :size="18" :stroke-width="2" />
                            <span class="mlb__section-title">{{ t('microlearning.incomplete_title') }}</span>
                            <span class="mlb__count">{{ incomplete.length }}</span>
                        </div>

                        <div class="mlb__list-block">
                            <ul class="mlb__list">
                                <li v-for="(title, index) in visibleItems" :key="index" class="mlb__item">
                                    <span class="mlb__bullet" aria-hidden="true"></span>
                                    <span class="mlb__item-text">{{ title }}</span>
                                </li>
                            </ul>

                            <button v-if="remaining > 0" type="button" class="mlb__more" @click="showMore">
                                <ChevronDown :size="16" :stroke-width="2" />
                                {{ t('microlearning.show_more', { count: remaining }) }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Footer CTA -->
                <div class="mlb__footer">
                    <a
                        :href="block.skills_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="mlb__cta"
                    >
                        <span>{{ t('microlearning.cta') }}</span>
                        <ArrowUpRight :size="16" :stroke-width="2.25" />
                    </a>
                    <p class="mlb__hint">{{ t('microlearning.restore_hint') }}</p>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
/* Everything here is plain scoped CSS — no Tailwind dependency — so the blocker
   renders identically on every page. AppLayout sets --app-navbar-height on :root;
   60px fallback covers first paint / navbar-less pages. */
.mlb {
    position: fixed;
    top: var(--app-navbar-height, 60px);
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2147483000;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow-y: auto;
    padding: 2rem 1rem;
    background: rgba(244, 245, 247, 0.96);
    backdrop-filter: blur(4px);
}

@media (min-width: 640px) {
    .mlb {
        align-items: center;
    }
}

.mlb__card {
    width: 100%;
    max-width: 34rem;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 20px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
}

.mlb__body {
    padding: 1.75rem;
}

/* Header */
.mlb__header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
}

.mlb__badge {
    flex: 0 0 auto;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: #d1fae5;
    color: #047857;
}

.mlb__header-text {
    min-width: 0;
}

.mlb__title {
    margin: 0;
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 600;
    color: #111827;
}

.mlb__intro {
    margin: 0.25rem 0 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #4b5563;
}

.mlb__link {
    font-weight: 600;
    color: #047857;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.mlb__link:hover {
    color: #065f46;
}

/* Modules section */
.mlb__section {
    margin-top: 1.5rem;
}

.mlb__section-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.mlb__section-icon {
    flex: 0 0 auto;
    color: #6b7280;
}

.mlb__section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
}

.mlb__count {
    margin-left: auto;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    background: #f3f4f6;
    font-size: 0.75rem;
    font-weight: 600;
    color: #4b5563;
}

/* Light gray list block, no border */
.mlb__list-block {
    background: #f5f6f7;
    border-radius: 12px;
    padding: 0.875rem 1rem;
}

.mlb__list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.mlb__item {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    padding: 0.25rem 0;
    font-size: 0.875rem;
    line-height: 1.4;
    color: #374151;
}

.mlb__bullet {
    flex: 0 0 auto;
    width: 6px;
    height: 6px;
    margin-top: 0.5rem;
    border-radius: 9999px;
    background: #10b981;
}

.mlb__item-text {
    min-width: 0;
}

.mlb__more {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.625rem;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
    color: #047857;
}

.mlb__more:hover {
    color: #065f46;
}

/* Footer */
.mlb__footer {
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
    padding: 1rem 1.75rem 1.25rem;
}

.mlb__cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.7rem 1rem;
    border-radius: 10px;
    background: #047857;
    color: #ffffff;
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    transition: background-color 0.15s ease;
}

.mlb__cta:hover {
    background: #065f46;
}

.mlb__cta:focus-visible {
    outline: 2px solid #10b981;
    outline-offset: 2px;
}

.mlb__hint {
    margin: 0.625rem 0 0;
    text-align: center;
    font-size: 0.75rem;
    color: #6b7280;
}
</style>
